// LAP-550 V1.2 — Page détail "Mes opportunités"
import React, { useEffect, useState, useMemo } from 'react'
import { Doughnut } from 'react-chartjs-2'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { useTranslation } from 'react-i18next'
import { makeGetRequest } from 'utils/api'
import DetailPageLayout from '../../_shared/DetailPageLayout'
import AssetTable from '../../_shared/AssetTable'
import { invTheme } from '../../_shared/theme'
import { textCol, moneyCol, deltaCol } from '../../_shared/gridCols'

const PALETTE = ['#f59e0b', '#63a0ff', '#22d3ee', '#a78bfa', '#34d399', '#f472b6', '#94a3b8']

export default function OpportunitiesDetail() {
  const { t } = useTranslation('layout')
  const [opps, setOpps] = useState([])
  const [view, setView] = useState('sector')
  const [search, setSearch] = useState('')

  useEffect(() => {
    const fetch = async () => {
      const r = await makeGetRequest('/api/investment/tracked-opportunitie-operations/?limit=200', {}, true).catch(() => null)
      if (r?.data?.results) setOpps(r.data.results)
    }
    fetch()
  }, [])

  const agg = useMemo(() => {
    const key = view
    const map = new Map()
    opps.forEach((o) => {
      const k = o[key] || o.industry || o.type_id || '—'
      map.set(k, (map.get(k) || 0) + 1)
    })
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1])
  }, [opps, view])

  const chartData = {
    labels: agg.map(([k]) => k),
    datasets: [
      {
        data: agg.map(([, v]) => v),
        backgroundColor: PALETTE,
        borderColor: 'rgba(0,0,0,0)',
        borderWidth: 2,
        hoverOffset: 6,
      },
    ],
  }
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      datalabels: {
        color: 'white',
        font: { size: 11, weight: 600 },
        formatter: (v, ctx) => `${ctx.chart.data.labels[ctx.dataIndex]}\n${v}`,
        display: 'auto',
        textAlign: 'center',
      },
      legend: { display: false },
    },
  }

  const columns = useMemo(
    () => [
      textCol({ field: 'type_id', headerName: t('investments.portfolio.cols.type'), width: 70 }),
      textCol({ field: 'name', headerName: t('investments.portfolio.cols.title'), width: 160 }),
      textCol({ field: 'sector', headerName: t('investments.detail.cols.sector', 'Secteur'), width: 130 }),
      moneyCol({ field: 'price', headerName: t('investments.portfolio.cols.price'), width: 110 }),
      moneyCol({ field: 'target_price', headerName: t('investments.portfolio.cols.targetPrice'), width: 120 }),
      moneyCol({ field: 'target_value', headerName: t('investments.portfolio.cols.targetValue'), width: 120 }),
      deltaCol({ field: 'value_change', headerName: t('investments.portfolio.cols.valueChange'), width: 120, unit: '' }),
      deltaCol({ field: 'value_change_pct', headerName: t('investments.portfolio.cols.valueChangePct'), width: 130 }),
    ],
    [t],
  )

  return (
    <DetailPageLayout
      title={t('investments.portfolio.opportunity.title')}
      subtitle={t('investments.detail.oppsSubtitle', 'Répartition par secteur')}
      icon={<VisibilityOutlinedIcon fontSize="small" />}
      accent={invTheme.accents.opportunity}
      chartSubtitleOptions={[
        { value: 'sector', label: t('investments.detail.bySector', 'Par secteur') },
        { value: 'type_id', label: t('investments.detail.byType', 'Par type') },
      ]}
      chartSubtitle={view}
      onChartSubtitleChange={setView}
      sumLabel={t('investments.detail.totalTracked', 'Suivies')}
      sumValue={opps.length}
      chart={<Doughnut data={chartData} options={chartOptions} />}
      tableTitle={t('investments.detail.oppsTableTitle', "Mon suivi d'opportunités")}
      tableCount={opps.length}
      tableContent={
        <AssetTable
          rows={opps}
          columns={columns}
          emptyLabel={t('investments.portfolio.noRows')}
          searchable
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t('investments.detail.searchOpportunities', 'Rechercher une opportunité, un secteur…')}
        />
      }
    />
  )
}
